import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/core/api/client';
import { useToast } from '@/shared/components/Toast';
import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  BestMatchHighlight,
  findBestMatchEntry,
  KeyResourcesPanel,
  KeyResourcesPanelSkeleton,
  NavigatorResults,
  navigatorResultsCopy,
  useNavigatorMatching,
  buildNavigatorViewModelFromLive,
  swapSelection,
  type NavigatorProvider,
  type NavigatorProviderContext,
  type FpQaResourceBundle,
} from '@/shared/components/navigator-results';
import type { ProviderProfile } from '@/features/provider-directory/types/provider-profile.types';
import { resolveBestMatches } from '@/shared/components/navigator-results/prototype/bestMatchPrototype.data';
import { useBestMatchPrototype } from '@/shared/components/navigator-results/prototype/useBestMatchPrototype';
import { BestMatchPrototypeSwitcher } from '@/shared/components/navigator-results/prototype/BestMatchPrototypeSwitcher';
import { VariantCSpotlightStrip } from '@/shared/components/navigator-results/prototype/VariantCSpotlightStrip';
import { downloadFpQaEngagementPdf } from '../../api/fpQaApi';
import { useFpQaEngagement, useRenameFpQaEngagement } from '../../hooks/useFpQaEngagements';
import { useFpQaProviderProfiles } from '../../hooks/useFpProviderProfiles';
import { trackFpQaEvent } from '../../utils/fpQaRum';
import { BackLink, Stepper } from './ResultsPageChrome';
import { FpProviderSlot } from './FpProviderSlot';
import { ConciergeBanner } from './ConciergeBanner';
import { ConciergeContactModal } from './ConciergeContactModal';
import { PlanNameEditor } from './PlanNameEditor';
import { ResultsActionsBar } from './ResultsActionsBar';
import { ShareCarePlanModal } from './ShareCarePlanModal';
import { ReferProviderDispatcher } from './ReferProviderDispatcher';
import { ShareContactCardModal } from '@/features/contact-card-share/components/ShareContactCardModal';

type ResultsPageProps = {
  engagementId: string;
  onBack: () => void;
};

export const ResultsPage = ({ engagementId, onBack }: ResultsPageProps) => {
  const { showToast } = useToast();
  const copy = navigatorResultsCopy.fp;
  const [activeIndex, setActiveIndex] = useState(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [conciergeOpen, setConciergeOpen] = useState(false);
  const [referProvider, setReferProvider] = useState<ProviderProfile | null>(null);
  // Feature 071: off-platform sibling of Refer — needs no selected client.
  const [contactCardProvider, setContactCardProvider] = useState<ProviderProfile | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);

  const { data: engagement, isLoading, isError } = useFpQaEngagement(engagementId);
  const renameMutation = useRenameFpQaEngagement(engagementId);

  // Drive the SAME live matching pipeline the care seeker uses, keyed off this
  // engagement's navigator session, so FP + CS render identical plans (same
  // categories, order and providers). The engagement row is metadata only; the
  // categories_json snapshot is rebuilt server-side at PDF/share time.
  const sessionId = engagement?.navigatorSessionId;
  const { status, categories: liveCategories } = useNavigatorMatching(sessionId);

  // Curated Key Resources for this navigator session — the same session-scoped
  // bundle the care seeker's results page uses. Session-based (not
  // engagement-based) because the engagement snapshot is empty until a freeze.
  const { data: resourceBundle, isLoading: resourceBundleLoading } = useQuery<FpQaResourceBundle>({
    queryKey: ['fp-qa-hub-resources-session', sessionId ?? ''],
    enabled: Boolean(sessionId),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data } = await apiClient.get<FpQaResourceBundle>('/find-providers/hub-resources', {
        params: { session_id: sessionId },
      });
      return {
        specialtyResourcesBySpecialtyId: data?.specialtyResourcesBySpecialtyId ?? {},
        careStageResources: data?.careStageResources ?? [],
      };
    },
  });

  // Provider ids for the active category come straight from the live matches, so
  // the rich MatchedProviderCard hydration stays in sync with what CS shows.
  const activeProviderIds = liveCategories[activeIndex]?.providers.map((p) => p.provider_id) ?? [];
  const profileQueries = useFpQaProviderProfiles(activeProviderIds);

  // PROTOTYPE (087 ticket 10) — enabled with ?bestMatchPrototype=A
  const bestMatchProto = useBestMatchPrototype();

  useEffect(() => {
    if (engagement) {
      trackFpQaEvent('fp_qa_results_loaded', {
        engagementId: engagement.id,
        clientUserId: engagement.clientUserId,
        careStageId: engagement.careStageId,
      });
    }
  }, [engagement]);

  // PROTOTYPE (087 ticket 10) — computed ahead of the loading early-return.
  const viewModel = buildNavigatorViewModelFromLive({
    planTitle: engagement?.name?.trim() || engagement?.careStageLabel || 'Care Guide',
    careStageLabel: engagement?.careStageLabel ?? '',
    summaryBullets: engagement?.summaryBullets ?? [],
    categories: liveCategories,
  });
  const bestMatch = bestMatchProto.enabled
    ? resolveBestMatches(viewModel, bestMatchProto.scenario)
    : undefined;

  // Feature 087 (ticket 16) — production Best Match, computed server-side on
  // `viewModel` (the live session data, never the prototype's mock/fixture
  // shell). Independent of the dev-only prototype override above.
  const liveBestMatch = findBestMatchEntry(viewModel);

  if (isLoading || !engagement) {
    return (
      <section className="space-y-6">
        <BackLink onBack={onBack} />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        {isError && (
          <p className="text-sm text-red-600">
            Couldn&apos;t load this engagement. It may have been deleted.
          </p>
        )}
      </section>
    );
  }

  const displayedTitle = engagement.name?.trim() || engagement.careStageLabel || 'Care Guide';
  const displayViewModel = bestMatch?.viewModel ?? viewModel;
  // Same rule as the care-seeker guide: render as soon as the specialty list
  // exists, then let providers land row by row. Gated on the view model that
  // actually renders, so a Best Match substitution cannot leave the page
  // stuck on the skeleton.
  const isMatching = status === 'idle' || status === 'running';
  const isBuilding = isMatching && displayViewModel.categories.length === 0;
  const activeCategory = displayViewModel.categories[activeIndex];

  const handleDownloadPdf = async () => {
    if (isPdfDownloading) return;
    setIsPdfDownloading(true);
    trackFpQaEvent('fp_qa_pdf_downloaded', {
      engagementId: engagement.id,
      clientUserId: engagement.clientUserId,
    });
    try {
      await downloadFpQaEngagementPdf(engagement.id, `${displayedTitle}.pdf`);
    } catch (err) {
      console.error('Failed to download FP Q&A PDF', err);
      window.alert('Sorry — we could not generate the PDF right now. Please try again in a moment.');
    } finally {
      setIsPdfDownloading(false);
    }
  };

  const handleShareClick = () => {
    trackFpQaEvent('fp_qa_share_dialog_opened', {
      engagementId: engagement.id,
      clientUserId: engagement.clientUserId,
    });
    setShareOpen(true);
  };

  const handleConcierge = () => {
    trackFpQaEvent('fp_qa_concierge_handoff_initiated', {
      engagementId: engagement.id,
      clientUserId: engagement.clientUserId,
      careStageId: engagement.careStageId,
    });
    setConciergeOpen(true);
  };

  const handleRefer = (profile: ProviderProfile) => {
    if (!activeCategory) return;
    setReferProvider(profile);
    trackFpQaEvent('fp_qa_provider_referred', {
      engagementId: engagement.id,
      specialtyId: activeCategory.id,
      providerId: profile.id,
      clientUserId: engagement.clientUserId,
    });
  };

  // Feature 080 — single-select swap. The shared store action is optimistic
  // and reverts itself on failure; RUM tracks the FP-side interaction.
  const handleSelectProvider = async (provider: NavigatorProvider, category: { id: string }) => {
    if (!sessionId) return;
    trackFpQaEvent('fp_qa_selection_swapped', {
      engagementId: engagement?.id,
      specialtyId: category.id,
      providerId: provider.id,
    });
    const ok = await swapSelection(sessionId, category.id, provider.id);
    if (!ok) showToast(copy.selectionSaveFailed, 'error');
  };

  const handleSaveName = () => {
    const next = nameDraft.trim() ? nameDraft.trim().slice(0, 200) : null;
    if ((next ?? '') === (engagement.name ?? '')) {
      setIsEditingName(false);
      return;
    }
    renameMutation.mutate(
      { name: next },
      {
        onSuccess: () => {
          setIsEditingName(false);
          trackFpQaEvent('fp_qa_engagement_renamed', { engagementId: engagement.id });
        },
      },
    );
  };

  const referProviderName =
    referProvider?.companyName?.trim() || referProvider?.user?.firstName || 'Provider';

  const renderProvider = (provider: NavigatorProvider, context: NavigatorProviderContext) => (
    <FpProviderSlot
      provider={provider}
      context={context}
      query={profileQueries[context.index]}
      specialtyName={activeCategory?.name ?? ''}
      canRefer
      onRefer={handleRefer}
      onSendContactCard={setContactCardProvider}
      isClientLinked={engagement.clientUserId !== null}
    />
  );

  return (
    <>
      {isBuilding ? (
        <section className="space-y-6">
          <BackLink onBack={onBack} />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Stepper />
          </div>
          <p className="font-termina text-xl font-medium text-text-primary">
            Building your care guide…
          </p>
          <Skeleton className="h-32 w-full rounded-2xl" />
          <Skeleton className="h-48 w-full rounded-2xl" />
        </section>
      ) : (
        <NavigatorResults
          viewModel={displayViewModel}
          isMatching={isMatching}
          copy={copy}
          activeIndex={activeIndex}
          onActiveIndexChange={setActiveIndex}
          topSlot={
            <>
              <BackLink onBack={onBack} />
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Stepper />
              </div>
            </>
          }
          beforeTabsSlot={
            bestMatchProto.enabled && bestMatch ? (
              <VariantCSpotlightStrip result={bestMatch} />
            ) : liveBestMatch ? (
              <BestMatchHighlight entry={liveBestMatch} />
            ) : undefined
          }
          bestMatchProviderIds={bestMatchProto.enabled ? bestMatch?.providerIds : undefined}
          titleSlot={
            <PlanNameEditor
              isEditing={isEditingName}
              displayedTitle={displayedTitle}
              nameDraft={nameDraft}
              isSaving={renameMutation.isPending}
              onStartEditing={() => {
                setNameDraft(engagement.name ?? '');
                setIsEditingName(true);
              }}
              onDraftChange={setNameDraft}
              onSave={handleSaveName}
              onCancel={() => setIsEditingName(false)}
            />
          }
          headerActions={
            <ResultsActionsBar
              isMatching={isMatching}
              isPdfDownloading={isPdfDownloading}
              onShare={handleShareClick}
              onDownloadPdf={handleDownloadPdf}
            />
          }
          footerSlot={<ConciergeBanner onRequest={handleConcierge} />}
          onConciergeRequest={handleConcierge}
          onSelectProvider={handleSelectProvider}
          renderProvider={renderProvider}
          renderKeyResources={(category) =>
            resourceBundleLoading ? (
              <KeyResourcesPanelSkeleton />
            ) : (
              <KeyResourcesPanel
                specialtyResources={resourceBundle?.specialtyResourcesBySpecialtyId[category.id] ?? []}
                careStageResources={resourceBundle?.careStageResources ?? []}
              />
            )
          }
          onAltsToggle={(open) => {
            if (open && activeCategory) {
              trackFpQaEvent('fp_qa_search_other_expanded', {
                engagementId: engagement.id,
                specialtyId: activeCategory.id,
              });
            }
          }}
        />
      )}

      <ConciergeContactModal open={conciergeOpen} onOpenChange={setConciergeOpen} />

      <ShareCarePlanModal
        open={shareOpen}
        engagementId={engagement.id}
        planLabel={displayedTitle}
        preselectClientUserId={engagement.clientUserId}
        onPreviewPdf={handleDownloadPdf}
        isPdfDownloading={isPdfDownloading}
        onClose={() => setShareOpen(false)}
      />

      {contactCardProvider?.id && (
        <ShareContactCardModal
          open
          onOpenChange={(open) => {
            if (!open) setContactCardProvider(null);
          }}
          serviceProviderId={contactCardProvider.id}
        />
      )}
      <ReferProviderDispatcher
        engagementClientUserId={engagement.clientUserId}
        referProviderId={referProvider?.id ?? null}
        referProviderName={referProviderName}
        onOpenChange={(open) => {
          if (!open) setReferProvider(null);
        }}
      />

      {bestMatchProto.enabled && <BestMatchPrototypeSwitcher controls={bestMatchProto} />}
    </>
  );
};
